<template>
  <main
    :class="[
      'landing-page',
      {
        'is-loaded': isLoaded
      }
    ]"
    @contextmenu.prevent
  >
    <div class="bg-layer" aria-hidden="true">
      <div class="bg-overlay" />
    </div>

    <section class="hero">
      <div class="hero-intro">
        <div class="brand-row">
          <img :src="logo" alt="Wall Labs logo" class="brand-logo" draggable="false" />
          <h1 class="brand-title">
            <span class="brand-wall">Wall</span>
            <span class="brand-labs">Labs</span>
          </h1>
        </div>

        <p class="hero-chip">NOW ON ANDROID & iOS</p>
      </div>

      <div class="showcase-stack">
        <div class="showcase-row">
          <div class="showcase-copy showcase-copy--one">
            <h2 class="showcase-headline">
              <span class="showcase-headline--gradient">NEW DROPS.</span>
              ENDLESS INSPIRATION.
            </h2>
            <p class="showcase-description">
              Premium 4K, HD, and AMOLED wallpapers curated for every mood: Sci-Fi, Nature,
              Anime-style, Developer, Motivation, and more.
            </p>
            <div class="store-buttons">
              <a
                :href="playStoreUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="store-link store-link--google"
              >
                <img :src="googlePlayBadge" alt="" class="store-link-icon" draggable="false" />
                <span class="store-link-text">
                  <span class="store-link-text-top">Get it on</span>
                  <span class="store-link-text-main">Google Play</span>
                </span>
              </a>
              <a
                :href="appStoreUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="store-link store-link--apple"
              >
                <img :src="appStoreBadge" alt="" class="store-link-icon" draggable="false" />
                <span class="store-link-text">
                  <span class="store-link-text-top">Download on the</span>
                  <span class="store-link-text-main">App Store</span>
                </span>
              </a>
            </div>
            <div class="rotating-quote" aria-live="polite">
              <p :class="['rotating-quote-text', { 'is-visible': quoteRotators.primary.isVisible }]">
                “{{ quoteRotators.primary.quotes[quoteRotators.primary.activeIndex] }}”
              </p>
            </div>
          </div>
          <div class="showcase-visual showcase-visual--one">
            <img
              :src="phoneCutout26"
              alt="Wall Labs app preview"
              class="phone-cutout protected-image"
              draggable="false"
            />
          </div>
        </div>

        <div class="showcase-row showcase-row--reverse">
          <div class="showcase-visual showcase-visual--two">
            <img
              :src="phoneCutout27"
              alt="Wall Labs app preview"
              class="phone-cutout protected-image"
              draggable="false"
            />
          </div>
          <div class="showcase-copy showcase-copy--two">
            <h2 class="showcase-headline">UPGRADE YOUR SCREEN. EVERY DAY.</h2>
            <p class="showcase-description">
              Handpicked 4K, HD, and AMOLED wallpapers for every mood and moment. Find your next
              favorite in our latest collections.
            </p>
            <div class="rotating-quote" aria-live="polite">
              <p :class="['rotating-quote-text', { 'is-visible': quoteRotators.secondary.isVisible }]">
                “{{ quoteRotators.secondary.quotes[quoteRotators.secondary.activeIndex] }}”
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="preview-section">
      <header class="preview-header" data-reveal>
        <p class="preview-eyebrow">CURATED FOR EVERY MOOD</p>
        <h2 class="preview-title">
          <span class="preview-title--gradient">EXPLORE</span> BY CATEGORY.
        </h2>
        <p class="preview-subtitle">
          Stunning 4K, HD, and AMOLED wallpapers across Sci-Fi, Nature, Anime-style, Developer,
          Motivation, and more.
        </p>
      </header>

      <div class="preview-row">
      <article
        v-for="(wall, index) in featuredWalls"
        :key="wall.id"
        class="preview-card"
        data-reveal
        :style="{ '--reveal-index': index }"
      >
        <div class="preview-image-wrap">
          <img
            :src="wall.url"
            :alt="wall.title"
            class="preview-image protected-image"
            draggable="false"
          />
        </div>
        <div class="preview-meta">
          <h3>{{ wall.title }}</h3>
          <p>{{ wall.category }}</p>
        </div>
      </article>
      </div>
    </section>

    <footer class="bottom-footer" data-reveal>
      <div class="footer-links">
        <span class="footer-label">Platforms:</span>
        <a :href="playStoreUrl" target="_blank" rel="noopener noreferrer">Android</a>
        <a :href="appStoreUrl" target="_blank" rel="noopener noreferrer">iOS</a>
      </div>
      <div class="footer-links">
        <span class="footer-label">Legal:</span>
        <router-link to="/privacy-policy">Privacy Policy</router-link>
      </div>
      <div class="footer-support">
        <span class="footer-label">Support:</span>
        <a href="mailto:contactus@mobil80.com">contactus@mobil80.com</a>
        <span>|</span>
        <a href="tel:+918041615842">080 41615842</a>
      </div>
      <p class="footer-copy">© {{ currentYear }} Wall Labs</p>
    </footer>
  </main>
</template>

<script>
import logo from '@/assets/wall-labs-logo.png'
import googlePlayBadge from '@/assets/google-play.png'
import appStoreBadge from '@/assets/appstore.png'
import phoneCutout26 from '@/assets/26.png'
import phoneCutout27 from '@/assets/27.png'
import { attachContentProtection } from '@/services/contentProtection'

export default {
  name: 'SupportLandingPage',
  data() {
    return {
      isLoaded: false,
      detachContentProtection: null,
      logo,
      googlePlayBadge,
      appStoreBadge,
      phoneCutout26,
      phoneCutout27,
      playStoreUrl:
        'https://play.google.com/store/apps/details?id=com.mobil80.walllabs&pcampaignid=web_share',
      appStoreUrl: 'https://apps.apple.com/in/app/wall-labs-wallpapers/id6770902129',
      quoteRotators: {
        primary: {
          quotes: [
            'Your phone deserves more than a default background.',
            'Your wallpaper is the first thing you see every time you unlock your phone.',
            'Your home screen is personal. Make it unforgettable.'
          ],
          activeIndex: 0,
          isVisible: true,
          intervalId: null,
          timeoutId: null
        },
        secondary: {
          quotes: [
            'Small screen. Infinite worlds.',
            'Curated for every mood. Designed for every unlock.',
            'The best wallpaper is the one that makes you pause for a second.'
          ],
          activeIndex: 0,
          isVisible: true,
          intervalId: null,
          timeoutId: null
        }
      },
      featuredWalls: [
        {
          id: 'w-amoled',
          title: 'Mystic Owl',
          category: 'AMOLED',
          url: 'https://walllabs-buckets.s3.amazonaws.com/wallpapers/Mystic%20Owl_AMOLED.png'
        },
        {
          id: 'w-scifi',
          title: 'The Edge',
          category: 'Sci-Fi',
          url: 'https://walllabs-buckets.s3.amazonaws.com/wallpapers/The%20Edge_Sci-Fi.png'
        },
        {
          id: 'w-nature',
          title: 'Tranquil Sunset',
          category: 'Nature',
          url: 'https://walllabs-buckets.s3.amazonaws.com/wallpapers/Tranquil%20Sunset_Nature.png'
        },
        {
          id: 'w-anime',
          title: 'Shadow Monarch',
          category: 'Anime-style',
          url: 'https://walllabs-buckets.s3.amazonaws.com/wallpapers/Shadow%20Monarch_Anime-style.png'
        },
        {
          id: 'w-developer',
          title: 'Vibe Coder',
          category: 'Developer',
          url: 'https://walllabs-buckets.s3.amazonaws.com/wallpapers/Vibe%20Coder_Developer.png'
        },
        {
          id: 'w-motivation',
          title: 'Not Done',
          category: 'Motivation',
          url: 'https://walllabs-buckets.s3.amazonaws.com/wallpapers/Not%20Done_Motivation.png'
        }
      ]
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    }
  },
  mounted() {
    requestAnimationFrame(() => {
      this.isLoaded = true
    })
    this.setupScrollReveal()
    this.detachContentProtection = attachContentProtection(this.$el)
    this.startQuoteRotations()
  },
  beforeUnmount() {
    this.scrollObserver?.disconnect()
    this.detachContentProtection?.()
    this.stopQuoteRotations()
  },
  methods: {
    startQuoteRotations() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

      this.startQuoteRotation('primary')
      this.startQuoteRotation('secondary')
    },
    startQuoteRotation(key) {
      const rotator = this.quoteRotators[key]
      rotator.intervalId = setInterval(() => this.cycleQuote(key), 2500)
    },
    stopQuoteRotations() {
      Object.keys(this.quoteRotators).forEach((key) => {
        const rotator = this.quoteRotators[key]

        if (rotator.intervalId) {
          clearInterval(rotator.intervalId)
          rotator.intervalId = null
        }

        if (rotator.timeoutId) {
          clearTimeout(rotator.timeoutId)
          rotator.timeoutId = null
        }
      })
    },
    cycleQuote(key) {
      const rotator = this.quoteRotators[key]
      rotator.isVisible = false

      rotator.timeoutId = setTimeout(() => {
        rotator.activeIndex = (rotator.activeIndex + 1) % rotator.quotes.length
        rotator.isVisible = true
        rotator.timeoutId = null
      }, 650)
    },
    setupScrollReveal() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.$el?.querySelectorAll('[data-reveal]').forEach((el) => {
          el.classList.add('is-visible')
        })
        return
      }

      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return
            entry.target.classList.add('is-visible')
            this.scrollObserver.unobserve(entry.target)
          })
        },
        { threshold: 0.12, rootMargin: '0px 0px -32px 0px' }
      )

      this.$nextTick(() => {
        this.$el?.querySelectorAll('[data-reveal]').forEach((el) => {
          this.scrollObserver.observe(el)
        })
      })
    }
  }
}
</script>

<style scoped>
.landing-page {
  position: relative;
  min-height: 100vh;
  color: #f8fafc;
  overflow: hidden;
  -webkit-user-select: none;
  user-select: none;
}

.landing-page a,
.landing-page button {
  -webkit-user-select: auto;
  user-select: auto;
}

.protected-image,
.landing-page img {
  -webkit-user-drag: none;
  user-drag: none;
  -webkit-touch-callout: none;
  pointer-events: none;
}

.store-link,
.store-link img {
  pointer-events: auto;
}

.bg-layer {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: #02030a;
  overflow: hidden;
}

.bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(165deg, #000000 0%, #050505 72%, #1a1a1a 100%);
}

.bg-overlay::before,
.bg-overlay::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.35;
  animation: ambientDrift 16s ease-in-out infinite alternate;
}

.bg-overlay::before {
  width: 420px;
  height: 420px;
  top: -120px;
  right: -80px;
  background: radial-gradient(circle, rgba(168, 85, 247, 0.45) 0%, transparent 70%);
}

.bg-overlay::after {
  width: 360px;
  height: 360px;
  bottom: -100px;
  left: -60px;
  background: radial-gradient(circle, rgba(96, 165, 250, 0.35) 0%, transparent 70%);
  animation-delay: -8s;
}

@keyframes ambientDrift {
  0% {
    transform: translate3d(0, 0, 0) scale(1);
  }

  100% {
    transform: translate3d(24px, -18px, 0) scale(1.08);
  }
}

.hero {
  position: relative;
  z-index: 1;
  padding: 26px clamp(20px, 4vw, 56px) 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.showcase-stack {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.hero-intro {
  max-width: 620px;
  padding-top: 8px;
  margin-bottom: 0;
}

.brand-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  opacity: 0;
  transform: translateY(16px);
  transition:
    opacity 0.75s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-chip {
  display: block;
  width: fit-content;
  margin: 0 0 16px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #dbeafe;
  border: 1px solid rgba(255, 255, 255, 0.26);
  background: rgba(255, 255, 255, 0.06);
  text-align: left;
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 0.75s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.75s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.75s ease,
    border-color 0.75s ease;
}

.showcase-copy {
  max-width: 560px;
  opacity: 0;
  transform: translateX(-28px);
  transition:
    opacity 0.85s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.85s cubic-bezier(0.22, 1, 0.36, 1);
}

.showcase-copy--two {
  transform: translateX(28px);
}

.showcase-visual {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  opacity: 0;
  transform: translateX(36px);
  transition:
    opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
}

.showcase-visual--two {
  transform: translateX(-36px);
}

.showcase-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;
  gap: clamp(20px, 4vw, 48px);
}

.showcase-row + .showcase-row {
  margin-top: -48px;
}

.showcase-stack > .showcase-row:last-child {
  margin-bottom: -96px;
}

.showcase-headline {
  margin: 0;
  font-size: clamp(2rem, 5vw, 3.9rem);
  line-height: 0.95;
  letter-spacing: -0.02em;
  font-weight: 900;
  text-shadow: 0 8px 30px rgba(0, 0, 0, 0.45);
  text-align: left;
}

.showcase-headline--gradient {
  background: linear-gradient(135deg, #f472b6 0%, #a855f7 50%, #60a5fa 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 8px 24px rgba(168, 85, 247, 0.28));
}

.showcase-description {
  margin: 16px 0 0;
  color: #d1d9ff;
  font-size: clamp(0.96rem, 1.4vw, 1.08rem);
  line-height: 1.7;
  text-align: left;
}

.brand-logo {
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
  padding: 6px;
  transition: transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
}

.brand-title {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-size: clamp(1.8rem, 2.4vw, 2.2rem);
  font-weight: 800;
}

.brand-wall {
  background: linear-gradient(135deg, #f472b6 0%, #a855f7 50%, #60a5fa 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.store-buttons {
  margin-top: 18px;
  margin-bottom: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  max-width: 402px;
}

.store-link {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  flex: 1 1 0;
  min-width: 195px;
  width: 195px;
  box-sizing: border-box;
  padding: 10px 16px 10px 14px;
  border-radius: 999px;
  border: 1px solid #ffffff;
  background: #ffffff;
  color: #111827;
  text-decoration: none;
  transition:
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.25s ease;
}

.store-link-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.05;
}

.store-link-text-top {
  font-size: 0.62rem;
  font-weight: 400;
  letter-spacing: 0.01em;
  color: #111827;
}

.store-link-text-main {
  font-size: 1.02rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #111827;
}

.store-link:hover {
  transform: translateY(-3px) scale(1.02);
}

.store-link:active {
  transform: translateY(-1px) scale(0.99);
}

.store-link--google {
  box-shadow: 0 8px 20px rgba(255, 255, 255, 0.2);
}

.store-link--google:hover {
  box-shadow: 0 14px 28px rgba(255, 255, 255, 0.28);
}

.store-link--apple {
  box-shadow: 0 8px 20px rgba(255, 255, 255, 0.2);
}

.store-link--apple:hover {
  box-shadow: 0 14px 28px rgba(255, 255, 255, 0.28);
}

.store-link-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.rotating-quote {
  margin-top: 146px;
  max-width: 460px;
  min-height: 5.8em;
}

.rotating-quote-text {
  margin: 0;
  color: #ffffff;
  font-size: clamp(1.08rem, 1.85vw, 1.24rem);
  font-style: italic;
  font-weight: 500;
  line-height: 1.6;
  opacity: 0;
  transform: translateY(12px);
  filter: blur(4px);
  transition:
    opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.7s cubic-bezier(0.4, 0, 0.2, 1),
    filter 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

.rotating-quote-text.is-visible {
  opacity: 1;
  transform: translateY(0);
  filter: blur(0);
}

.phone-cutout {
  display: block;
  width: min(380px, 100%);
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 18px 36px rgba(0, 0, 0, 0.58));
  transition: filter 0.35s ease;
}

.is-loaded .phone-cutout {
  animation: phoneGlow 4s ease-in-out 0.6s infinite alternate;
}

.showcase-stack > .showcase-row:first-child .phone-cutout {
  width: min(440px, 100%);
  transform: translateY(-56px);
}

.showcase-stack > .showcase-row:last-child .phone-cutout {
  width: min(520px, 100%);
  transform: translateY(-100px);
}

.preview-section {
  position: relative;
  z-index: 1;
  margin-top: -80px;
  padding: 0 clamp(20px, 4vw, 56px) 28px;
}

.preview-header {
  max-width: 720px;
  margin: 0 auto 28px;
  text-align: center;
  opacity: 0;
  transform: translateY(24px);
  transition:
    opacity 0.75s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
}

.preview-header.is-visible {
  opacity: 1;
  transform: none;
}

.preview-eyebrow {
  margin: 0 0 10px;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #c4b5fd;
  text-transform: uppercase;
}

.preview-title {
  margin: 0;
  font-size: clamp(1.85rem, 4vw, 3rem);
  line-height: 0.98;
  letter-spacing: -0.02em;
  font-weight: 900;
  text-shadow: 0 8px 30px rgba(0, 0, 0, 0.45);
  color: #ffffff;
}

.preview-title--gradient {
  background: linear-gradient(135deg, #f472b6 0%, #a855f7 50%, #60a5fa 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 8px 24px rgba(168, 85, 247, 0.28));
}

.preview-subtitle {
  margin: 14px auto 0;
  max-width: 620px;
  color: #d1d9ff;
  font-size: clamp(0.96rem, 1.4vw, 1.08rem);
  line-height: 1.7;
}

.preview-row {
  position: relative;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.preview-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(9, 14, 34, 0.72);
  opacity: 0;
  transform: translateY(28px) scale(0.98);
  transition:
    opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.65s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.3s ease,
    box-shadow 0.3s ease;
  transition-delay: calc(var(--reveal-index, 0) * 90ms);
}

.preview-card.is-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.preview-card.is-visible:hover {
  transform: translateY(-6px) scale(1.01);
  border-color: rgba(167, 139, 250, 0.45);
  box-shadow: 0 18px 40px rgba(96, 165, 250, 0.18);
}

.preview-image-wrap {
  overflow: hidden;
  aspect-ratio: 3 / 4;
}

.preview-image {
  width: 100%;
  height: 100%;
  aspect-ratio: auto;
  object-fit: cover;
  display: block;
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.preview-card.is-visible:hover .preview-image {
  transform: scale(1.06);
}

.preview-meta {
  position: relative;
  z-index: 1;
  padding: 10px 12px 12px;
  background: rgba(9, 14, 34, 0.96);
}

.preview-meta h3 {
  margin: 0 0 3px;
  font-size: 0.95rem;
  color: #ffffff;
}

.preview-meta p {
  margin: 0;
  font-size: 0.82rem;
  color: #c4b5fd;
}

.bottom-footer {
  position: relative;
  z-index: 1;
  margin: 24px clamp(20px, 4vw, 56px) 24px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(7, 12, 30, 0.72);
  padding: 14px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  justify-content: space-between;
  align-items: center;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.bottom-footer.is-visible {
  opacity: 1;
  transform: none;
}

.is-loaded .brand-row {
  opacity: 1;
  transform: none;
}

.is-loaded .brand-logo {
  transform: scale(1.04);
}

.is-loaded .hero-chip {
  opacity: 1;
  transform: none;
  animation: chipGlow 3.5s ease-in-out 1s infinite;
}

.is-loaded .showcase-copy--one {
  opacity: 1;
  transform: none;
  transition-delay: 0.22s;
}

.is-loaded .showcase-visual--one {
  opacity: 1;
  transform: none;
  transition-delay: 0.34s;
}

.is-loaded .showcase-visual--two {
  opacity: 1;
  transform: none;
  transition-delay: 0.46s;
}

.is-loaded .showcase-copy--two {
  opacity: 1;
  transform: none;
  transition-delay: 0.58s;
}

@keyframes chipGlow {
  0%,
  100% {
    box-shadow: 0 0 0 rgba(96, 165, 250, 0);
    border-color: rgba(255, 255, 255, 0.26);
  }

  50% {
    box-shadow: 0 0 22px rgba(96, 165, 250, 0.28);
    border-color: rgba(147, 197, 253, 0.45);
  }
}

@keyframes phoneGlow {
  0% {
    filter: drop-shadow(0 18px 36px rgba(0, 0, 0, 0.58));
  }

  100% {
    filter: drop-shadow(0 22px 44px rgba(96, 165, 250, 0.22));
  }
}

@media (prefers-reduced-motion: reduce) {
  .bg-overlay::before,
  .bg-overlay::after {
    animation: none;
  }

  .brand-row,
  .hero-chip,
  .showcase-copy,
  .showcase-visual,
  .preview-card,
  .preview-header,
  .bottom-footer {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
    animation: none !important;
  }

  .phone-cutout {
    animation: none !important;
  }
}

.footer-links {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.footer-links a {
  color: #e9ddff;
  text-decoration: none;
  font-size: 0.87rem;
  font-weight: 600;
  transition: color 0.2s ease;
}

.footer-links a:hover {
  color: #ffffff;
  text-decoration: underline;
}

.footer-support {
  color: #e5ecff;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.86rem;
}

.footer-label {
  color: #ffffff;
  font-weight: 700;
}

.footer-support a {
  color: #d8e7ff;
  text-decoration: none;
  transition: color 0.2s ease;
}

.footer-support a:hover {
  color: #ffffff;
  text-decoration: underline;
}

.footer-copy {
  margin: 0;
  color: #94a3b8;
  font-size: 0.8rem;
}

@media (max-width: 980px) {
  .showcase-row,
  .showcase-row--reverse {
    grid-template-columns: 1fr;
  }

  .showcase-row--reverse .showcase-visual {
    order: -1;
  }

  .showcase-copy {
    max-width: 100%;
  }

  .preview-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 620px) {
  .phone-cutout {
    width: min(320px, 92%);
  }

  .showcase-stack > .showcase-row:first-child .phone-cutout {
    width: min(360px, 92%);
    transform: translateY(-36px);
  }

  .showcase-stack > .showcase-row:last-child .phone-cutout {
    width: min(420px, 92%);
    transform: translateY(-100px);
  }

  .showcase-stack > .showcase-row:last-child {
    margin-bottom: -72px;
  }

  .preview-section {
    margin-top: -56px;
  }

  .preview-row {
    grid-template-columns: 1fr;
  }
}
</style>
